import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { useAuthStore } from '../store/authStore';
import { fetchTeams, createTeam, deleteTeam, updateTeam, addPlayer, deletePlayer, fetchGames, createGame, deleteGame, fetchSeasons, createSeason, updateGame } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Plus, Trash2, Users, Play, Calendar as CalendarIcon, Trophy, Crown, Shield, TrendingUp, ScanLine, LogOut, GitBranch, FileText, BookOpen, Settings, Sparkles, X, UserPlus, MessageSquare, Mail } from 'lucide-react';
import { cn } from '../lib/utils';
import { checkTeamAccess } from '../lib/api';
import { ScanLineupDialog } from '../components/shared/ScanLineupDialog';
import WeatherWidget from '../components/shared/WeatherWidget';
import GameDayCountdown from '../components/shared/GameDayCountdown';
import ScheduleManager from '../components/shared/ScheduleManager';
import { isRestEnforced } from '../lib/pitcherRest';
import { LATEST_WHATS_NEW } from '../lib/whatsNew';
import { lookupForecast } from '../lib/weatherCodes';
import { usePremium } from '../hooks/usePremium';
import WhosHotCard from '../components/shared/WhosHotCard';
import InviteParentsDialog from '../components/shared/InviteParentsDialog';
import ReferralCard from '../components/shared/ReferralCard';
import ShareReferralChip from '../components/shared/ShareReferralChip';
import InstallAppPrompt from '../components/shared/InstallAppPrompt';
import DeleteAccountDialog from '../components/shared/DeleteAccountDialog';
import { isNativeApp } from '../lib/platform';

export default function HomePage() {
  const navigate = useNavigate();
  const { user, subscriptionTier, subscriptionActive, isAdmin, getMaxTeams, logout, token } = useAuthStore();
  const isNative = isNativeApp();
  const { 
    sportType, setSportType,
    teams, setTeams, addTeam, removeTeam,
    games, setGames, addGame, removeGame,
    seasons, setSeasons, addSeason,
    currentTeamId, setCurrentTeam, currentGameId, setCurrentGame,
    addPlayerToTeam, removePlayerFromTeam
  } = useGameStore();
  
  const [loading, setLoading] = useState(true);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [teamSettingsDialog, setTeamSettingsDialog] = useState({ open: false, team: null });
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [whatsNewDismissed, setWhatsNewDismissed] = useState(() => {
    try { return localStorage.getItem('whats_new_dismissed') === LATEST_WHATS_NEW.id; } catch { return false; }
  });
  const { trialActive, trialDaysLeft, subscriptionActive: premiumSubActive, isPremium, isAdmin: premiumIsAdmin } = usePremium();
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      return localStorage.getItem('trial_banner_dismissed_date') === today;
    } catch { return false; }
  });
  const [gameDialogOpen, setGameDialogOpen] = useState(false);
  const [seasonDialogOpen, setSeasonDialogOpen] = useState(false);
  const [selectedTeamForPlayer, setSelectedTeamForPlayer] = useState(null);
  
  // Form states
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDpFlex, setNewTeamDpFlex] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerNumber, setNewPlayerNumber] = useState('');
  const [newPlayerIsPitcher, setNewPlayerIsPitcher] = useState(false);
  
  const [newGameOpponent, setNewGameOpponent] = useState('');
  const [newGameDate, setNewGameDate] = useState(new Date());
  const [newGameSeason, setNewGameSeason] = useState('');
  const [newGameIsHome, setNewGameIsHome] = useState(true);
  const [newGameFieldPerInning, setNewGameFieldPerInning] = useState(false);
  const [newGameTime, setNewGameTime] = useState('');
  const [newGameLocation, setNewGameLocation] = useState('');
  const [scheduleEntries, setScheduleEntries] = useState([]);
  
  const [ageGroup, setAgeGroup] = useState('11-12');
  const [customPitchLimit, setCustomPitchLimit] = useState('');

  const [newSeasonName, setNewSeasonName] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [newSeasonYear, setNewSeasonYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsData, gamesData, seasonsData] = await Promise.all([
        fetchTeams(),
        fetchGames(),
        fetchSeasons()
      ]);
      setTeams(teamsData);
      setGames(gamesData);
      setSeasons(seasonsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }
    if (teams.length >= 3) {
      toast.error('Maximum 3 teams allowed');
      return;
    }
    try {
      const team = await createTeam({
        name: newTeamName,
        sport_type: sportType,
        age_group: ageGroup,
        custom_pitch_limit: ageGroup === 'custom' ? parseInt(customPitchLimit) || 85 : null,
        dp_flex_enabled: sportType === 'softball' && newTeamDpFlex,
      });
      addTeam(team);
      setNewTeamName('');
      setNewTeamDpFlex(false);
      setTeamDialogOpen(false);
      toast.success('Team created!');
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const handleToggleRestRules = async (team, newValue) => {
    try {
      const updated = await updateTeam(team.id, { enforce_pitch_rest_rules: newValue });
      setTeams(teams.map(t => t.id === team.id ? updated : t));
      toast.success(`Rest rules ${newValue ? 'ON' : 'OFF'} for ${team.name}`);
    } catch {
      toast.error('Failed to update rest rules');
    }
  };

  const cancelGame = async (game, defaultReason = 'Rain') => {
    const reason = window.prompt(`Cancel "${game.opponent_name}" — reason?\n(e.g., Rain, Field closed, Forfeit)`, defaultReason);
    if (reason === null) return;
    try {
      await updateGame(game.id, { is_canceled: true, cancel_reason: reason || 'Canceled' });
      const fresh = await fetchGames();
      setGames(fresh);
      toast.success('Game marked canceled');
    } catch { toast.error('Failed to cancel game'); }
  };

  const handleDeleteTeam = async (teamId) => {
    const teamToDelete = teams.find(t => t.id === teamId);
    const teamName = teamToDelete?.name || 'this team';
    if (!window.confirm(`Delete "${teamName}"? This cannot be undone.`)) return;
    try {
      await deleteTeam(teamId);
      removeTeam(teamId);
      toast.success('Team deleted');
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const handleAddPlayer = async () => {
    if (!newPlayerName.trim()) {
      toast.error('Please enter a player name');
      return;
    }
    const team = teams.find(t => t.id === selectedTeamForPlayer);
    if (team && team.players?.length >= 50) {
      toast.error('Team roster is full (max 50 players)');
      return;
    }
    try {
      const player = await addPlayer(selectedTeamForPlayer, {
        name: newPlayerName,
        jersey_number: newPlayerNumber,
        is_pitcher: newPlayerIsPitcher
      });
      addPlayerToTeam(selectedTeamForPlayer, player);
      setNewPlayerName('');
      setNewPlayerNumber('');
      setNewPlayerIsPitcher(false);
      setPlayerDialogOpen(false);
      toast.success('Player added!');
      loadData();
    } catch (error) {
      toast.error('Failed to add player');
    }
  };

  const handleDeletePlayer = async (teamId, playerId) => {
    try {
      await deletePlayer(teamId, playerId);
      removePlayerFromTeam(teamId, playerId);
      toast.success('Player removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove player');
    }
  };

  const handleScannedPlayers = async (players) => {
    if (!currentTeamId) { toast.error('Select a team first'); return; }
    try {
      for (const p of players) {
        const existing = currentTeam?.players?.find(tp => tp.name.toLowerCase() === p.name.toLowerCase());
        if (!existing) {
          const created = await addPlayer(currentTeamId, { name: p.name, jersey_number: p.jersey_number || undefined });
          addPlayerToTeam(currentTeamId, created);
        }
      }
      loadData();
    } catch (err) {
      toast.error('Error adding scanned players');
    }
  };


  const handleCreateGame = async () => {
    if (!newGameOpponent.trim() || !currentTeamId) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      const game = await createGame({
        team_id: currentTeamId,
        opponent_name: newGameOpponent,
        sport_type: sportType,
        date: format(newGameDate, 'yyyy-MM-dd'),
        game_time: newGameTime || null,
        field_location: newGameLocation || null,
        season: newGameSeason || `${sportType === 'baseball' ? 'Spring' : 'Fall'} ${new Date().getFullYear()}`,
        is_home: newGameIsHome,
        field_positions_per_inning: newGameFieldPerInning
      });
      addGame(game);
      setCurrentGame(game.id);
      setNewGameOpponent('');
      setGameDialogOpen(false);
      toast.success('Game created!');
      navigate('/game-setup');
    } catch (error) {
      toast.error('Failed to create game');
    }
  };

  const handleDeleteGame = async (gameId) => {
    try {
      await deleteGame(gameId);
      removeGame(gameId);
      toast.success('Game deleted');
    } catch (error) {
      toast.error('Failed to delete game');
    }
  };

  const sendGameReminder = async (game) => {
    if (!window.confirm(`Send a game-day reminder email to all parents following ${currentTeam?.name || 'this team'}?`)) return;
    try {
      const r = await import('../lib/api').then(m => m.default.post(`/games/${game.id}/send-reminder`));
      const { sent, skipped, errors, no_followers } = r.data;
      if (no_followers) {
        toast.warning('No parents are following this team yet. Invite them via the Invite Parents button!');
      } else if (sent > 0) {
        toast.success(`Reminder sent to ${sent} parent${sent === 1 ? '' : 's'}!${skipped ? ` (${skipped} already notified)` : ''}`);
      } else if (skipped > 0) {
        toast.info(`All ${skipped} parent${skipped === 1 ? ' was' : 's were'} already notified for this game.`);
      } else {
        toast.info('No new reminders to send.');
      }
      if (errors > 0) toast.error(`${errors} email${errors === 1 ? '' : 's'} failed to send.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reminders');
    }
  };

  const handleCreateSeason = async () => {
    if (!newSeasonName.trim() || !currentTeamId) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const season = await createSeason({
        name: newSeasonName,
        year: newSeasonYear,
        team_id: currentTeamId,
        sport_type: sportType
      });
      addSeason(season);
      setNewSeasonName('');
      setSeasonDialogOpen(false);
      toast.success('Season created!');
    } catch (error) {
      toast.error('Failed to create season');
    }
  };

  const handleSelectGame = (gameId) => {
    setCurrentGame(gameId);
    navigate('/game-setup');
  };

  const [isMasterCoach, setIsMasterCoach] = useState(false);
  const [weatherDaily, setWeatherDaily] = useState(null); // { dates: ['2026-02-18',...], probs: [30,45,...], codes: [...], locName }

  useEffect(() => {
    if (currentTeamId) {
      checkTeamAccess(currentTeamId).then(data => {
        setIsMasterCoach(data.is_master_coach);
      }).catch(() => {});
    }
  }, [currentTeamId]);

  // Fetch weather forecast once for rain-aware game card hints
  useEffect(() => {
    try {
      const saved = localStorage.getItem('dmp_weather_location');
      if (!saved) return;
      const loc = JSON.parse(saved);
      if (!loc?.lat || !loc?.lon) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/weather?lat=${loc.lat}&lon=${loc.lon}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data?.daily) return;
          setWeatherDaily({
            dates: data.daily.time || [],
            probs: data.daily.precipitation_probability_max || [],
            codes: data.daily.weather_code || [],
            tmax: data.daily.temperature_2m_max || [],
            tmin: data.daily.temperature_2m_min || [],
            locName: loc.name,
          });
        })
        .catch(() => {});
    } catch {}
  }, []);

  const getGameRainInfo = (gameDateStr) => {
    if (!weatherDaily || !gameDateStr) return null;
    const idx = weatherDaily.dates.indexOf(gameDateStr.slice(0, 10));
    if (idx === -1) return null;
    const prob = weatherDaily.probs[idx] || 0;
    const code = weatherDaily.codes[idx];
    const severe = [65, 82, 95, 96, 99].includes(code);
    if (!severe && prob < 40) return null;
    return { prob, code, severe };
  };

  const currentTeam = teams.find(t => t.id === currentTeamId);
  const teamGames = games.filter(g => g.team_id === currentTeamId);
  const teamSeasons = seasons.filter(s => s.team_id === currentTeamId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8"
      style={{
        background: 'radial-gradient(ellipse at top, #F8FAFC 0%, #E5E7EB 60%, #D1D5DB 100%)',
      }}
      data-testid="home-page">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header — official lockup using the 3D DS monogram */}
        <div className="flex items-start justify-between">
          <div className="flex-1 flex flex-col items-center gap-3 text-center">
            <div className="flex items-center gap-3">
              <img
                src="/diamond-scorer-logo.jpg"
                alt="Diamond Scorer"
                width={56}
                height={56}
                style={{
                  borderRadius: 12,
                  boxShadow: '0 0 16px rgba(46,190,107,0.4), 0 2px 6px rgba(0,0,0,0.3)',
                  objectFit: 'cover',
                }}
              />
              <h1 className="text-4xl md:text-5xl font-black tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '-0.02em' }}>
                <span style={{ color: '#0E6B36' }}>DIAMOND</span>
                <span style={{ color: '#D4A03A' }} className="italic"> SCORER</span>
              </h1>
            </div>
            <p className="text-base md:text-lg italic font-bold tracking-wide" style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              color: '#B88C2E',
            }}>
              Score Smarter, Not Harder
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <ShareReferralChip />
            <DeleteAccountDialog />
            <Button variant="outline" onClick={() => { logout(); window.location.href = '/'; }} className="text-stone-500 hover:text-red-600 hover:border-red-300" data-testid="home-logout-btn">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>

        {/* Subscription Banner — premium gold-on-midnight, on-brand & dismissible-by-action */}
        {!isNative && !subscriptionActive && !isAdmin && (
          <Card className="border-0 shadow-lg overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0A1322 0%, #1A2542 100%)',
              border: '1px solid rgba(212, 160, 58, 0.3)',
            }}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #D4A03A 0%, #F5C455 100%)', boxShadow: '0 0 18px rgba(212,160,58,0.4)' }}>
                  <Crown className="w-6 h-6 text-stone-900" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.3px' }}>Unlock Premium</h3>
                  <p className="text-slate-300 text-sm">Spray charts · AI Scan · PDF reports · Unlimited teams</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/subscription')}
                className="font-bold text-stone-900 shadow"
                style={{ background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)' }}
                data-testid="subscription-banner-btn"
              >
                View Plans
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Admin Mode — slim pill instead of full-bleed red banner */}
        {isAdmin && (
          <div className="flex items-center justify-between gap-3 px-3 py-1.5 rounded-full text-sm shadow-sm w-fit"
            style={{
              background: 'rgba(212, 160, 58, 0.12)',
              border: '1px solid rgba(212, 160, 58, 0.45)',
              color: '#F5C455',
            }}
            data-testid="admin-mode-pill"
          >
            <Crown className="w-4 h-4 shrink-0" />
            <span className="font-bold tracking-wide text-xs uppercase">Admin Mode</span>
            <Button
              size="sm"
              className="h-7 px-2 ml-1 text-stone-900 font-bold"
              style={{ background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)' }}
              onClick={() => navigate('/admin')}
              data-testid="admin-panel-link"
            >
              <Shield size={12} className="mr-1" /> Panel
            </Button>
          </div>
        )}

        {/* Install App Prompt - helps parents & coaches add to homescreen */}
        <InstallAppPrompt variant="banner" />

        {/* Trial Ending Banner — dismissible once per day, escalates urgency */}
        {trialActive && !premiumSubActive && !premiumIsAdmin && !trialBannerDismissed && (
          <Card
            className={cn(
              "border-0 shadow-md text-white transition-all",
              trialDaysLeft <= 1
                ? "bg-gradient-to-r from-red-700 to-red-900 animate-pulse"
                : trialDaysLeft <= 3
                ? "bg-gradient-to-r from-orange-600 to-amber-700"
                : "bg-gradient-to-r from-amber-600 to-amber-700"
            )}
            data-testid="trial-ending-banner"
          >
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm sm:text-base leading-tight">
                  {trialDaysLeft <= 0
                    ? 'Your free trial ends today'
                    : trialDaysLeft === 1
                    ? 'Last day of your free trial'
                    : `${trialDaysLeft} days left in your free trial`}
                </p>
                <p className="text-white/85 text-xs sm:text-sm leading-snug mt-0.5">
                  {trialDaysLeft <= 1
                    ? 'Lock in Premium now to keep AI Scan, PDF Reports, Spray Charts, and push alerts for your followers.'
                    : 'Keep AI Scan, PDF Report Cards, Spray Charts, and push alerts after your trial ends.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/upgrade')}
                className="shrink-0 px-3 sm:px-4 py-2 rounded-lg bg-white text-amber-800 hover:bg-amber-50 font-bold text-xs sm:text-sm shadow transition-colors whitespace-nowrap"
                data-testid="trial-banner-upgrade-btn"
              >
                Upgrade
              </button>
              <button
                type="button"
                className="shrink-0 w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
                onClick={() => {
                  try {
                    const today = new Date().toISOString().slice(0, 10);
                    localStorage.setItem('trial_banner_dismissed_date', today);
                  } catch {}
                  setTrialBannerDismissed(true);
                }}
                data-testid="trial-banner-dismiss"
                aria-label="Dismiss"
              >
                <X size={16} />
              </button>
            </CardContent>
          </Card>
        )}

        {/* Trial Expired / Free-tier nudge — show when not admin, no sub, no active trial */}
        {!isNative && !trialActive && !premiumSubActive && !premiumIsAdmin && !isPremium && !trialBannerDismissed && (
          <Card
            className="border-0 shadow-md bg-gradient-to-r from-stone-800 to-stone-900 text-white"
            data-testid="trial-expired-banner"
          >
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Sparkles size={20} className="text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm sm:text-base">Your free trial has ended</p>
                <p className="text-stone-300 text-xs sm:text-sm leading-snug mt-0.5">
                  Upgrade to Premium to unlock AI Scan, PDF Report Cards, Spray Charts, and push alerts for your followers.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/upgrade')}
                className="shrink-0 px-3 sm:px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold text-xs sm:text-sm whitespace-nowrap"
                data-testid="trial-expired-upgrade-btn"
              >
                Upgrade
              </button>
              <button
                type="button"
                className="shrink-0 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                onClick={() => {
                  try {
                    const today = new Date().toISOString().slice(0, 10);
                    localStorage.setItem('trial_banner_dismissed_date', today);
                  } catch {}
                  setTrialBannerDismissed(true);
                }}
                data-testid="trial-expired-dismiss"
                aria-label="Dismiss"
              >
                <X size={16} />
              </button>
            </CardContent>
          </Card>
        )}

        {/* What's New Banner (dismissible) — uses official emerald + gold accent */}
        {!whatsNewDismissed && LATEST_WHATS_NEW && (
          <Card className="border-0 shadow-lg text-white overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0E6B36 0%, #1FA85A 100%)',
              border: '1px solid rgba(212, 160, 58, 0.3)',
            }}
            data-testid="whats-new-banner">
            <CardContent className="p-3 sm:p-4 flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(212, 160, 58, 0.25)', border: '1px solid rgba(212, 160, 58, 0.5)' }}>
                <Sparkles size={18} style={{ color: '#F5C455' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold"
                    style={{ background: 'rgba(245, 196, 85, 0.95)', color: '#0A1322' }}>New</span>
                  <p className="font-bold text-sm sm:text-base truncate">{LATEST_WHATS_NEW.title}</p>
                </div>
                <p className="text-emerald-50 text-xs sm:text-sm leading-snug">{LATEST_WHATS_NEW.blurb}</p>
              </div>
              <button
                type="button"
                className="shrink-0 w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
                onClick={() => {
                  try { localStorage.setItem('whats_new_dismissed', LATEST_WHATS_NEW.id); } catch {}
                  setWhatsNewDismissed(true);
                }}
                data-testid="whats-new-dismiss"
                aria-label="Dismiss announcement"
              >
                <X size={16} />
              </button>
            </CardContent>
          </Card>
        )}

        {/* Game Day Countdown - MOST PROMINENT */}
        {currentTeamId && (teamGames.length > 0 || scheduleEntries.length > 0) && (
          <GameDayCountdown
            games={[...teamGames, ...scheduleEntries.filter(e => e.status === 'scheduled').map(e => ({
              ...e, is_complete: false
            }))]}
            teamName={currentTeam?.name || ''}
          />
        )}

        {/* Who's Hot — season scouting card (coach can hide) */}
        {currentTeamId && (
          <WhosHotCard
            teamId={currentTeamId}
            teamName={currentTeam?.name}
            showToggle={true}
            isCoach={isMasterCoach || isAdmin}
            compact={true}
          />
        )}

        {/* Weather Widget - PROMINENT */}
        <WeatherWidget />

        {/* Season Schedule */}
        {currentTeamId && (
          <ScheduleManager teamId={currentTeamId} token={token} onScheduleLoaded={setScheduleEntries} />
        )}

        {/* Tournament Quick Access */}
        <Card className="border-0 shadow-md bg-white hover:border-amber-400 border-2 border-transparent cursor-pointer transition-all" onClick={() => navigate('/tournaments')} data-testid="tournament-quick-access">
          <CardContent className="py-3 px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <GitBranch size={18} className="text-amber-600" />
              </div>
              <div>
                <p className="font-bold text-sm text-stone-800">Tournament Brackets</p>
                <p className="text-xs text-stone-500">Build brackets, track pools, crown champions</p>
              </div>
            </div>
            <Trophy size={20} className="text-amber-500" />
          </CardContent>
        </Card>

        {/* Teams Section */}
        <Card className="border-0 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Users className="inline mr-2" size={20} />
              YOUR TEAMS ({teams.length}/{getMaxTeams()})
            </CardTitle>
            <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="create-team-btn"
                  className="bg-green-900 hover:bg-green-800"
                  disabled={teams.length >= getMaxTeams()}
                >
                  <Plus size={16} className="mr-1" /> New Team
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Team</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="teamName">Team Name</Label>
                    <Input
                      id="teamName"
                      data-testid="team-name-input"
                      placeholder="e.g., Tigers"
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Sport</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Button
                        type="button"
                        variant={sportType === 'baseball' ? 'default' : 'outline'}
                        className={cn(
                          'h-11 font-bold',
                          sportType === 'baseball'
                            ? 'bg-green-900 hover:bg-green-800 text-white'
                            : 'border-stone-300 text-stone-700'
                        )}
                        onClick={() => setSportType('baseball')}
                        data-testid="sport-baseball-btn"
                      >
                        Baseball
                      </Button>
                      <Button
                        type="button"
                        variant={sportType === 'softball' ? 'default' : 'outline'}
                        className={cn(
                          'h-11 font-bold',
                          sportType === 'softball'
                            ? 'bg-amber-600 hover:bg-amber-500 text-white'
                            : 'border-stone-300 text-stone-700'
                        )}
                        onClick={() => setSportType('softball')}
                        data-testid="sport-softball-btn"
                      >
                        Softball
                      </Button>
                    </div>
                  </div>
                  {/* DP/Flex toggle - softball only */}
                  {sportType === 'softball' && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="dp-flex-section">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-amber-600"
                          checked={newTeamDpFlex}
                          onChange={(e) => setNewTeamDpFlex(e.target.checked)}
                          data-testid="dp-flex-toggle"
                        />
                        <span className="text-sm font-bold text-stone-800">Enable DP/FLEX (10-player lineup)</span>
                      </label>
                      <p className="text-[11px] text-stone-600 mt-1.5 leading-snug pl-6">
                        Fastpitch rule: <strong>DP</strong> bats but doesn&apos;t field; <strong>FLEX</strong> fields
                        but doesn&apos;t bat. Lets you put your strongest hitter and strongest fielder on the field at the
                        same time. Leave off for slowpitch / non-DP leagues.
                      </p>
                    </div>
                  )}
                  <div>
                    <Label>Age Group (Pitch Count Rules)</Label>
                    <select
                      value={ageGroup}
                      onChange={(e) => setAgeGroup(e.target.value)}
                      className="w-full mt-1 rounded-md border border-stone-300 bg-white px-3 py-2 text-sm"
                      data-testid="age-group-select"
                    >
                      <option value="7-8">Ages 7-8 (50 pitches/day)</option>
                      <option value="9-10">Ages 9-10 (75 pitches/day)</option>
                      <option value="11-12">Ages 11-12 (85 pitches/day)</option>
                      <option value="13-16">Ages 13-16 (95 pitches/day)</option>
                      <option value="custom">Custom limit</option>
                    </select>
                  </div>
                  {ageGroup === 'custom' && (
                    <div>
                      <Label>Custom Pitch Limit</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 100"
                        value={customPitchLimit}
                        onChange={(e) => setCustomPitchLimit(e.target.value)}
                        data-testid="custom-pitch-limit-input"
                      />
                    </div>
                  )}
                  {teams.length >= getMaxTeams() && !isAdmin && (
                    <p className="text-sm text-amber-600">
                      Upgrade to Pro to create more teams
                    </p>
                  )}
                  <Button 
                    data-testid="submit-team-btn"
                    onClick={handleCreateTeam} 
                    className="w-full bg-green-900 hover:bg-green-800"
                  >
                    Create Team
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {teams.length === 0 ? (
              <p className="text-center text-stone-500 py-8">No teams yet. Create your first team!</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {teams.map((team) => (
                  <Card 
                    key={team.id}
                    data-testid={`team-card-${team.id}`}
                    className={cn(
                      'cursor-pointer transition-all border-2',
                      currentTeamId === team.id 
                        ? 'border-green-700 bg-green-50' 
                        : 'border-transparent hover:border-stone-300'
                    )}
                    onClick={() => setCurrentTeam(team.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-lg">{team.name}</h3>
                        <div className="flex items-center gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`team-settings-${team.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTeamSettingsDialog({ open: true, team });
                            }}
                            title="Team Settings"
                          >
                            <Settings size={16} className="text-stone-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`delete-team-${team.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTeam(team.id);
                            }}
                          >
                            <Trash2 size={16} className="text-red-500" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-stone-500">
                        {team.players?.length || 0} players • {team.sport_type} • Ages {team.age_group || '11-12'}
                      </p>
                      {/* Quick rest-rules toggle */}
                      <button
                        type="button"
                        className={cn(
                          "mt-2 w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-[11px] font-semibold transition-all border",
                          isRestEnforced(team)
                            ? "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
                            : "bg-stone-100 text-stone-500 border-stone-300 hover:bg-stone-200"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleRestRules(team, !isRestEnforced(team));
                        }}
                        data-testid={`rest-rules-toggle-${team.id}`}
                        title="Little League-style pitcher rest day enforcement"
                      >
                        <span className="flex items-center gap-1 truncate">
                          <Shield size={11} />
                          <span className="truncate">Little League Rest Rules</span>
                        </span>
                        <span className={cn(
                          "px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0",
                          isRestEnforced(team) ? "bg-amber-600 text-white" : "bg-stone-400 text-white"
                        )}>
                          {isRestEnforced(team) ? 'ON' : 'OFF'}
                        </span>
                      </button>
                      {currentTeamId === team.id && (
                        <p className="text-xs text-green-700 font-semibold mt-2">✓ Selected</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invite Parents + Message Team — shown only for coaches */}
        {currentTeam && (isMasterCoach || isAdmin) && (
          <div className="grid grid-cols-2 gap-3">
            <Button
              className="h-14 bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold shadow-md flex-col items-center gap-0.5 text-xs"
              onClick={() => setInviteDialogOpen(true)}
              data-testid="invite-parents-btn"
            >
              <UserPlus size={18} />
              <span>Invite Parents</span>
            </Button>
            <Button
              className="h-14 bg-green-700 hover:bg-green-600 text-white font-bold shadow-md flex-col items-center gap-0.5 text-xs"
              onClick={() => navigate('/messages')}
              data-testid="message-team-btn"
            >
              <MessageSquare size={18} />
              <span>Message Team</span>
            </Button>
          </div>
        )}

        {/* Referral / viral growth card — coaches only */}
        {(isMasterCoach || isAdmin) && <ReferralCard />}

        {/* Players Section - Show only if team selected */}
        {currentTeam && (
          <Card className="border-0 shadow-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-xl" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {currentTeam.name.toUpperCase()} ROSTER ({currentTeam.players?.length || 0}/15)
              </CardTitle>
              <div className="flex gap-2">
                <Button onClick={() => setScanOpen(true)} variant="outline" className="border-green-500 text-green-700 hover:bg-green-50" data-testid="home-scan-lineup-btn">
                  <ScanLine size={16} className="mr-1" /> Scan Card
                </Button>
                <Dialog open={playerDialogOpen} onOpenChange={setPlayerDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="add-player-btn"
                      className="bg-amber-600 hover:bg-amber-500"
                      onClick={() => setSelectedTeamForPlayer(currentTeamId)}
                      disabled={(currentTeam.players?.length || 0) >= 50}
                    >
                      <Plus size={16} className="mr-1" /> Add Player
                    </Button>
                  </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Player to {currentTeam.name}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="playerName">Player Name</Label>
                      <Input
                        id="playerName"
                        data-testid="player-name-input"
                        placeholder="e.g., John Smith"
                        value={newPlayerName}
                        onChange={(e) => setNewPlayerName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="playerNumber">Jersey Number</Label>
                      <Input
                        id="playerNumber"
                        data-testid="player-number-input"
                        placeholder="e.g., 10"
                        value={newPlayerNumber}
                        onChange={(e) => setNewPlayerNumber(e.target.value)}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="isPitcher"
                        data-testid="player-is-pitcher-checkbox"
                        checked={newPlayerIsPitcher}
                        onCheckedChange={setNewPlayerIsPitcher}
                      />
                      <Label htmlFor="isPitcher">Can pitch</Label>
                    </div>
                    <Button 
                      data-testid="submit-player-btn"
                      onClick={handleAddPlayer} 
                      className="w-full bg-green-900 hover:bg-green-800"
                    >
                      Add Player
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {!currentTeam.players?.length ? (
                <p className="text-center text-stone-500 py-8">No players yet. Add players to your roster!</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {currentTeam.players.map((player) => (
                    <div
                      key={player.id}
                      data-testid={`player-item-${player.id}`}
                      className="bg-white border border-stone-200 rounded-sm p-3 flex items-center gap-2 shadow-sm"
                    >
                      <div className="w-10 h-10 rounded-sm bg-green-900 text-white flex items-center justify-center font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {player.jersey_number || '—'}
                      </div>
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/player-profile/${currentTeamId}/${player.id}`)} data-testid={`player-profile-link-${player.id}`}>
                        <p className="font-semibold text-sm truncate hover:text-green-700 transition-colors">{player.name}</p>
                        {player.is_pitcher && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1 rounded">P</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-player-${player.id}`}
                        onClick={() => handleDeletePlayer(currentTeamId, player.id)}
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Manage Coaches - Show for master coach or admin */}
        {currentTeam && (isMasterCoach || isAdmin) && (
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-green-700" />
                <div>
                  <h3 className="font-bold text-stone-800">Manage Coaches</h3>
                  <p className="text-stone-500 text-sm">Add assistant coaches by phone number</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/coaches')}
                className="bg-green-900 hover:bg-green-800"
                data-testid="manage-coaches-btn"
              >
                <Users size={16} className="mr-1" /> Manage
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Season Stats Card */}
        {currentTeam && (
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-green-700" />
                <div>
                  <h3 className="font-bold text-stone-800">Season Stats</h3>
                  <p className="text-stone-500 text-sm">Batting leaders, pitching totals, team record</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/season-stats')}
                className="bg-green-900 hover:bg-green-800"
                data-testid="season-stats-btn"
              >
                View Stats
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Pre-Game Setup Quick Access */}
        {currentTeam && (
          <Card className="border-0 shadow-md bg-white hover:border-green-500 border-2 border-transparent cursor-pointer transition-all" 
            onClick={() => {
              if (currentGameId) {
                navigate('/game-setup');
              } else {
                toast.info('Create a new game first, then set up your pre-game info');
                setGameDialogOpen(true);
              }
            }} 
            data-testid="pregame-setup-card">
            <CardContent className="py-3 px-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <FileText size={18} className="text-green-700" />
                </div>
                <div>
                  <p className="font-bold text-sm text-stone-800">Pre-Game Setup</p>
                  <p className="text-xs text-stone-500">Lineups, fielding, opponent info — enter what you have</p>
                </div>
              </div>
              <Play size={20} className="text-green-600" />
            </CardContent>
          </Card>
        )}

        {/* Games Section */}
        {currentTeam && (
          <Card className="border-0 shadow-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-xl" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <Play className="inline mr-2" size={20} />
                GAMES
              </CardTitle>
              <div className="flex gap-2">
                <Dialog open={seasonDialogOpen} onOpenChange={setSeasonDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="create-season-btn">
                      <Trophy size={16} className="mr-1" /> New Season
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Season</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label>Season Name</Label>
                        <Input
                          data-testid="season-name-input"
                          placeholder="e.g., Spring League"
                          value={newSeasonName}
                          onChange={(e) => setNewSeasonName(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Year</Label>
                        <Input
                          type="number"
                          data-testid="season-year-input"
                          value={newSeasonYear}
                          onChange={(e) => setNewSeasonYear(parseInt(e.target.value))}
                        />
                      </div>
                      <Button 
                        data-testid="submit-season-btn"
                        onClick={handleCreateSeason}
                        className="w-full bg-green-900 hover:bg-green-800"
                      >
                        Create Season
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Dialog open={gameDialogOpen} onOpenChange={setGameDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="create-game-btn"
                      className="bg-green-900 hover:bg-green-800"
                    >
                      <Plus size={16} className="mr-1" /> New Game
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Game</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label>Opponent</Label>
                        <Input
                          data-testid="game-opponent-input"
                          placeholder="e.g., Eagles"
                          value={newGameOpponent}
                          onChange={(e) => setNewGameOpponent(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              data-testid="game-date-picker"
                              className="w-full justify-start text-left font-normal"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(newGameDate, 'PPP')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={newGameDate}
                              onSelect={(date) => date && setNewGameDate(date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Game Time</Label>
                          <Input
                            type="time"
                            data-testid="game-time-input"
                            value={newGameTime}
                            onChange={(e) => setNewGameTime(e.target.value)}
                            placeholder="3:30 PM"
                          />
                        </div>
                        <div>
                          <Label>Field / Location</Label>
                          <Input
                            data-testid="game-location-input"
                            placeholder="e.g., Anderson Field"
                            value={newGameLocation}
                            onChange={(e) => setNewGameLocation(e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Season</Label>
                        <Select value={newGameSeason} onValueChange={setNewGameSeason}>
                          <SelectTrigger data-testid="game-season-select">
                            <SelectValue placeholder="Select season" />
                          </SelectTrigger>
                          <SelectContent>
                            {teamSeasons.map((s) => (
                              <SelectItem key={s.id} value={`${s.name} ${s.year}`}>
                                {s.name} {s.year}
                              </SelectItem>
                            ))}
                            <SelectItem value={`${sportType === 'baseball' ? 'Spring' : 'Fall'} ${new Date().getFullYear()}`}>
                              Default ({sportType === 'baseball' ? 'Spring' : 'Fall'} {new Date().getFullYear()})
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="isHome"
                          data-testid="game-is-home-checkbox"
                          checked={newGameIsHome}
                          onCheckedChange={setNewGameIsHome}
                        />
                        <Label htmlFor="isHome">Home game</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="fieldPerInning"
                          data-testid="game-field-per-inning-checkbox"
                          checked={newGameFieldPerInning}
                          onCheckedChange={setNewGameFieldPerInning}
                        />
                        <Label htmlFor="fieldPerInning">Set field positions each inning</Label>
                      </div>
                      <Button 
                        data-testid="submit-game-btn"
                        onClick={handleCreateGame}
                        className="w-full bg-green-900 hover:bg-green-800"
                      >
                        Create Game
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* === STREAM SETUP READINESS CHIP ===
                  Passive game-day-readiness signal. Checks whether ANY game on
                  this team has ever had a live_stream_url set. If yes → emerald
                  "Ready" chip. If no → amber "Set up streaming" CTA that opens
                  the existing TV blog + onboarding. Stays out of the way: just a
                  single slim row above the games list. */}
              {currentTeamId && (() => {
                const hasStreamSetup = teamGames.some(g => (g.live_stream_url || '').trim().length > 0);
                if (hasStreamSetup) {
                  return (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-md text-xs"
                      style={{ background: 'rgba(46, 190, 107, 0.08)', border: '1px solid rgba(46, 190, 107, 0.35)' }}
                      data-testid="stream-status-ready"
                    >
                      <span className="text-base">✅</span>
                      <span className="font-bold" style={{ color: '#0E6B36' }}>Stream Setup Ready</span>
                      <span className="text-stone-500">·</span>
                      <span className="text-stone-600">You&apos;ve broadcast before — coaches can re-use a saved URL or set a new one per game.</span>
                    </div>
                  );
                }
                return (
                  <button
                    type="button"
                    onClick={() => {
                      // Open the most recent upcoming game's Go Live wizard. If
                      // no games yet, just hint at it — coach needs to create one first.
                      const upcoming = teamGames.find(g => !g.is_complete && !g.is_canceled);
                      if (upcoming) {
                        setCurrentGame(upcoming.id);
                        navigate('/game-mode');
                      } else {
                        toast.info('Create a game first, then tap GO LIVE to set up streaming.');
                      }
                    }}
                    className="flex items-center gap-2 w-full mb-3 px-3 py-2 rounded-md text-xs text-left hover:scale-[1.005] transition-all"
                    style={{ background: 'rgba(212, 160, 58, 0.1)', border: '1px solid rgba(212, 160, 58, 0.5)' }}
                    data-testid="stream-status-not-set"
                  >
                    <span className="text-base">⚠️</span>
                    <span className="font-bold" style={{ color: '#B88C2E' }}>Streaming not set up yet</span>
                    <span className="text-stone-500">·</span>
                    <span className="text-stone-700 flex-1">Set it up <span className="font-semibold">before</span> game day so you&apos;re not scrambling courtside.</span>
                    <span className="font-bold text-[10px] uppercase tracking-wider px-2 py-1 rounded shrink-0"
                      style={{ background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)', color: '#0A1322' }}>
                      Set up →
                    </span>
                  </button>
                );
              })()}
              {teamGames.length === 0 ? (
                <p className="text-center text-stone-500 py-8">No games yet. Create your first game!</p>
              ) : (
                <div className="space-y-2">
                  {teamGames.map((game) => (
                    <div
                      key={game.id}
                      data-testid={`game-item-${game.id}`}
                      className="bg-white border border-stone-200 rounded-sm p-4 shadow-sm hover:border-green-500 transition-all"
                    >
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => handleSelectGame(game.id)}>
                        <div>
                          <p className="font-bold">
                            {game.is_home ? `vs ${game.opponent_name}` : `@ ${game.opponent_name}`}
                          </p>
                          <p className="text-sm text-stone-500">
                            {game.date} • {game.season}
                            {game.game_time && ` • ${(() => { const [h,m] = game.game_time.split(':').map(Number); return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`; })()}`}
                          </p>
                          {/* Inline per-game forecast — only for upcoming/in-progress games within the 7-day window */}
                          {!game.is_complete && !game.is_canceled && (() => {
                            const fc = lookupForecast(weatherDaily, game.date);
                            if (!fc) return null;
                            const { info, tmax, tmin, prob } = fc;
                            const F = info.Icon;
                            return (
                              <p className="flex items-center gap-1.5 text-[11px] text-stone-500 mt-0.5" data-testid={`game-forecast-${game.id}`}>
                                <F size={12} className={info.color} />
                                <span className="font-medium">{info.label}</span>
                                {typeof tmax === 'number' && typeof tmin === 'number' && (
                                  <span className="text-stone-400">· {Math.round(tmax)}°/{Math.round(tmin)}°</span>
                                )}
                                {prob >= 20 && (
                                  <span className={prob >= 40 ? 'text-amber-600 font-semibold' : 'text-blue-500'}>
                                    · {prob}% rain
                                  </span>
                                )}
                              </p>
                            );
                          })()}
                        </div>
                        <div className="flex items-center gap-2">
                          {game.is_canceled ? (
                            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-sm font-bold">CANCELED</span>
                          ) : game.is_complete ? (
                            <span className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                              {game.home_score} - {game.away_score}
                            </span>
                          ) : (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-sm">
                              In Progress
                            </span>
                          )}
                        </div>
                      </div>
                      {game.is_canceled && game.cancel_reason && (
                        <p className="text-xs text-amber-700 mt-1 italic">Reason: {game.cancel_reason}</p>
                      )}
                      {/* Rain risk banner — only for upcoming/in-progress games that are not already canceled/complete */}
                      {!game.is_canceled && !game.is_complete && (() => {
                        const rain = getGameRainInfo(game.date);
                        if (!rain) return null;
                        return (
                          <button
                            type="button"
                            className={cn(
                              "mt-2 w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-semibold transition-all border",
                              rain.severe
                                ? "bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                                : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
                            )}
                            onClick={(e) => { e.stopPropagation(); cancelGame(game, rain.severe ? 'Severe Weather' : 'Rain'); }}
                            data-testid={`rain-cancel-hint-${game.id}`}
                            title="Weather-aware cancel shortcut"
                          >
                            <span className="shrink-0">{rain.severe ? '⛈' : '☔'}</span>
                            <span className="flex-1 text-left truncate">
                              {rain.severe ? 'Severe weather' : `${rain.prob}% rain`} at game time{weatherDaily?.locName ? ` (${weatherDaily.locName})` : ''} — tap to Cancel
                            </span>
                            <span className={cn(
                              "px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0",
                              rain.severe ? "bg-red-600 text-white" : "bg-amber-600 text-white"
                            )}>
                              {rain.severe ? 'SEVERE' : 'RAIN'}
                            </span>
                          </button>
                        );
                      })()}
                      {/* Action buttons row */}
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-stone-100">
                        {!game.is_complete && !game.is_canceled && (
                          <>
                            <Button size="sm" variant="outline" className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                              data-testid={`pregame-btn-${game.id}`}
                              onClick={(e) => { e.stopPropagation(); setCurrentGame(game.id); navigate('/game-setup'); }}>
                              <FileText size={12} className="mr-1" /> Pre-Game
                            </Button>
                            <Button size="sm" className="text-xs bg-green-700 hover:bg-green-600"
                              data-testid={`start-scoring-btn-${game.id}`}
                              onClick={async (e) => { e.stopPropagation(); setCurrentGame(game.id); try { await updateGame(game.id, { is_top_inning: true }); } catch {} navigate('/game-mode'); }}>
                              <Play size={12} className="mr-1" /> Start Scoring
                            </Button>
                            {(isMasterCoach || isAdmin) && (
                              <Button size="sm" variant="outline" className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                                data-testid={`remind-parents-${game.id}`}
                                onClick={(e) => { e.stopPropagation(); sendGameReminder(game); }}
                                title="Send a game-day reminder email to all parents following this team">
                                <Mail size={12} className="mr-1" /> Remind
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                              data-testid={`cancel-game-${game.id}`}
                              onClick={(e) => { e.stopPropagation(); cancelGame(game, 'Rain'); }}>
                              <X size={12} className="mr-1" /> Cancel
                            </Button>
                          </>
                        )}
                        {game.is_canceled && (
                          <Button size="sm" variant="outline" className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                            data-testid={`restore-game-${game.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await updateGame(game.id, { is_canceled: false, cancel_reason: null });
                                const fresh = await fetchGames();
                                setGames(fresh);
                                toast.success('Game restored');
                              } catch { toast.error('Failed to restore game'); }
                            }}>
                            Restore
                          </Button>
                        )}
                        {game.is_complete && (
                          <Button variant="outline" size="sm" className="text-xs"
                            data-testid={`view-summary-${game.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/game-summary/${currentTeamId}/${game.id}`); }}>
                            Summary
                          </Button>
                        )}
                        <div className="flex-1" />
                        <Button variant="ghost" size="sm"
                          data-testid={`delete-game-${game.id}`}
                          onClick={(e) => { e.stopPropagation(); handleDeleteGame(game.id); }}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      <ScanLineupDialog open={scanOpen} onOpenChange={setScanOpen} onPlayersScanned={handleScannedPlayers} />

      {/* Team Settings Dialog */}
      <Dialog open={teamSettingsDialog.open} onOpenChange={(o) => setTeamSettingsDialog({ open: o, team: o ? teamSettingsDialog.team : null })}>
        <DialogContent data-testid="team-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings size={18} /> {teamSettingsDialog.team?.name} Settings
            </DialogTitle>
          </DialogHeader>
          {teamSettingsDialog.team && (
            <div className="space-y-4 pt-2">
              <div className="text-sm text-stone-600">
                <span className="font-semibold">Sport:</span> {teamSettingsDialog.team.sport_type}
                <span className="mx-2">•</span>
                <span className="font-semibold">Age:</span> {teamSettingsDialog.team.age_group || '11-12'}
              </div>

              <div className="rounded-lg border p-3 bg-stone-50">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-semibold text-sm flex items-center gap-1.5">
                      <Shield size={14} className="text-amber-700" />
                      Little League Rest Rules
                    </p>
                    <p className="text-xs text-stone-600 mt-1">
                      When <span className="font-bold">ON</span>, coaches see a warning before using a pitcher who hasn't had their required rest days (per Little League pitch-count rules).
                      <br />
                      Turn <span className="font-bold">OFF</span> for softball, travel ball, or any league that doesn't enforce rest days. Pitch count tracking still works.
                    </p>
                  </div>
                  <button
                    type="button"
                    className={cn(
                      "shrink-0 w-14 h-7 rounded-full relative transition-colors",
                      isRestEnforced(teamSettingsDialog.team) ? "bg-amber-600" : "bg-stone-300"
                    )}
                    onClick={() => {
                      const next = !isRestEnforced(teamSettingsDialog.team);
                      handleToggleRestRules(teamSettingsDialog.team, next);
                      setTeamSettingsDialog({ open: true, team: { ...teamSettingsDialog.team, enforce_pitch_rest_rules: next } });
                    }}
                    data-testid="settings-rest-rules-toggle"
                    aria-label="Toggle rest rules"
                  >
                    <span className={cn(
                      "absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform",
                      isRestEnforced(teamSettingsDialog.team) ? "translate-x-7" : "translate-x-0.5"
                    )} />
                  </button>
                </div>
                <div className="mt-2 text-[10px] font-bold uppercase tracking-wider">
                  <span className={isRestEnforced(teamSettingsDialog.team) ? "text-amber-700" : "text-stone-500"}>
                    Currently: {isRestEnforced(teamSettingsDialog.team) ? 'ON' : 'OFF'}
                  </span>
                  {teamSettingsDialog.team.enforce_pitch_rest_rules === null || teamSettingsDialog.team.enforce_pitch_rest_rules === undefined
                    ? <span className="text-stone-400"> (auto-default for {teamSettingsDialog.team.sport_type})</span>
                    : null}
                </div>
              </div>

              {/* DP/FLEX toggle — softball teams only */}
              {teamSettingsDialog.team.sport_type === 'softball' && (
                <div className="border rounded-lg p-3 bg-amber-50/60" data-testid="settings-dp-flex-section">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="font-bold text-sm">DP/FLEX (10-player lineup)</Label>
                    <button
                      type="button"
                      onClick={async () => {
                        const next = !teamSettingsDialog.team.dp_flex_enabled;
                        try {
                          const updated = await updateTeam(teamSettingsDialog.team.id, { dp_flex_enabled: next });
                          setTeams(teams.map(t => t.id === updated.id ? updated : t));
                          setTeamSettingsDialog({ open: true, team: updated });
                          toast.success(`DP/FLEX ${next ? 'enabled' : 'disabled'}`);
                        } catch { toast.error('Update failed'); }
                      }}
                      className={cn(
                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                        teamSettingsDialog.team.dp_flex_enabled ? "bg-amber-600" : "bg-stone-300"
                      )}
                      data-testid="settings-dp-flex-toggle"
                    >
                      <span className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white transition",
                        teamSettingsDialog.team.dp_flex_enabled ? "translate-x-6" : "translate-x-1"
                      )} />
                    </button>
                  </div>
                  <p className="text-[11px] text-stone-600 leading-snug">
                    DP bats but doesn&apos;t field; FLEX fields but doesn&apos;t bat. Recognized as positions in the lineup card and AI scan.
                  </p>
                </div>
              )}

              <Button
                className="w-full"
                variant="outline"
                onClick={() => setTeamSettingsDialog({ open: false, team: null })}
                data-testid="settings-close-btn"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite Parents Dialog */}
      {currentTeam && (
        <InviteParentsDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          teamId={currentTeamId}
          teamName={currentTeam.name}
        />
      )}
    </div>
  );
}
